import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { 
  Compass, ArrowLeft, Users, Calendar, DollarSign, 
  CheckCircle, XCircle, Clock, FileText, ExternalLink,
  Filter, RefreshCw, ToggleLeft, ToggleRight
} from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  getTours, getAdminTourReservations, updateTourReservation, 
  updateTourAvailability, getTourStats 
} from "@/lib/api";

export default function AdminTours() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tours, setTours] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  
  // Check admin session
  useEffect(() => {
    const role = sessionStorage.getItem("userRole");
    if (role !== "admin") {
      navigate("/admin");
    } else {
      loadData();
    }
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [toursRes, reservationsRes, statsRes] = await Promise.all([
        getTours(),
        getAdminTourReservations({ status: statusFilter === "all" || statusFilter === "active" ? undefined : statusFilter }),
        getTourStats()
      ]);
      setTours(toursRes.data);
      // Filter for "active" (pending + confirmed) reservations
      const filteredReservations = statusFilter === "active" 
        ? reservationsRes.data.filter(r => r.status === "pending" || r.status === "confirmed")
        : reservationsRes.data;
      setReservations(filteredReservations);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load tour data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      loadData();
    }
  }, [statusFilter]);

  const handleToggleAvailability = async (tourId, currentAvailable) => {
    try {
      await updateTourAvailability(tourId, !currentAvailable);
      toast.success(`Tour ${!currentAvailable ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (error) {
      console.error("Error updating availability:", error);
      toast.error("Failed to update tour availability");
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedReservation) return;
    
    try {
      setUpdating(true);
      await updateTourReservation(selectedReservation.id, {
        status: newStatus,
        admin_notes: adminNotes
      });
      toast.success(`Reservation ${newStatus}`);
      setSelectedReservation(null);
      setAdminNotes("");
      loadData();
    } catch (error) {
      console.error("Error updating reservation:", error);
      toast.error("Failed to update reservation");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      confirmed: "bg-green-100 text-green-800 border-green-300",
      rejected: "bg-red-100 text-red-800 border-red-300",
      cancelled: "bg-gray-100 text-gray-800 border-gray-300"
    };
    return statusStyles[status] || statusStyles.pending;
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Compass className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading tour management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to="/admin/dashboard"
            className="inline-flex items-center text-muted-foreground hover:text-primary mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground mb-2">
                Tour Management
              </h1>
              <p className="text-muted-foreground">
                Manage tour reservations and availability
              </p>
            </div>
            <Button onClick={loadData} variant="outline" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-700">Pending</p>
                    <p className="text-3xl font-bold text-yellow-800">{stats.pending_reservations}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700">Confirmed</p>
                    <p className="text-3xl font-bold text-green-800">{stats.confirmed_upcoming}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700">Today's Tours</p>
                    <p className="text-3xl font-bold text-blue-800">{stats.today_tours_count}</p>
                  </div>
                  <Compass className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-700">Today's Participants</p>
                    <p className="text-3xl font-bold text-purple-800">{stats.today_participants}</p>
                  </div>
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reservations List */}
        <Card className="mb-6 bg-card border-border shadow-card" data-testid="reservations-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Tour Reservations
                </CardTitle>
                <CardDescription>
                  {reservations.length} reservation(s)
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active (Pending & Confirmed)</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {reservations.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No reservations found
              </p>
            ) : (
              <div className="space-y-4">
                {reservations.map((res) => (
                  <div 
                    key={res.id}
                    className="p-4 rounded-lg bg-background/50 border border-border"
                    data-testid={`reservation-row-${res.id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold">{res.tour_name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {res.guest_name} • {res.cabin_name}
                        </p>
                      </div>
                      <Badge className={cn("border", getStatusBadge(res.status))}>
                        {res.status.charAt(0).toUpperCase() + res.status.slice(1)}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-muted-foreground">Date:</span>
                        <p className="font-medium">
                          {format(parse(res.date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Participants:</span>
                        <p className="font-medium">{res.num_participants}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total:</span>
                        <p className="font-medium">${res.total_price.toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Deposit:</span>
                        <p className="font-medium">
                          {res.deposit_required ? (
                            <>
                              ${res.deposit_amount.toFixed(2)} 
                              {res.deposit_confirmed 
                                ? <span className="text-green-600 ml-1">✓</span>
                                : <span className="text-red-600 ml-1">⏳</span>
                              }
                            </>
                          ) : (
                            "N/A"
                          )}
                        </p>
                      </div>
                    </div>

                    {res.participant_names && res.participant_names.length > 0 && (
                      <p className="text-sm text-muted-foreground mb-2">
                        <strong>Participants:</strong> {res.participant_names.join(", ")}
                      </p>
                    )}

                    {res.special_requests && (
                      <p className="text-sm text-muted-foreground mb-2">
                        <strong>Requests:</strong> {res.special_requests}
                      </p>
                    )}

                    {res.admin_notes && (
                      <p className="text-sm text-blue-600 mb-2">
                        <strong>Admin Notes:</strong> {res.admin_notes}
                      </p>
                    )}

                    {/* Passport Links */}
                    {res.passport_files && res.passport_files.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {res.passport_files.map((file, index) => (
                          <a
                            key={index}
                            href={`${BACKEND_URL}${file}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          >
                            <FileText className="w-3 h-3" />
                            Passport {index + 1}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Action Buttons */}
                    {res.status === "pending" && (
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedReservation(res);
                            setAdminNotes(res.admin_notes || "");
                          }}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`confirm-btn-${res.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedReservation(res);
                            setAdminNotes(res.admin_notes || "");
                          }}
                          data-testid={`reject-btn-${res.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tour Availability */}
        <Card className="mb-6 bg-card border-border shadow-card" data-testid="availability-card">
          <CardHeader>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Compass className="w-5 h-5 text-primary" />
              Tour Availability
            </CardTitle>
            <CardDescription>
              Enable or disable tours (e.g., for weather, park closures)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tours.map((tour) => (
                <div 
                  key={tour.id}
                  className={cn(
                    "p-4 rounded-lg border transition-colors",
                    tour.available 
                      ? "bg-green-50 border-green-200" 
                      : "bg-gray-50 border-gray-200"
                  )}
                  data-testid={`tour-availability-${tour.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{tour.name}</h4>
                      <p className="text-xs text-muted-foreground">${tour.price}</p>
                    </div>
                    <button
                      onClick={() => handleToggleAvailability(tour.id, tour.available)}
                      className={cn(
                        "ml-2 p-1 rounded transition-colors",
                        tour.available 
                          ? "text-green-600 hover:bg-green-100" 
                          : "text-gray-400 hover:bg-gray-200"
                      )}
                      data-testid={`toggle-${tour.id}`}
                    >
                      {tour.available ? (
                        <ToggleRight className="w-8 h-8" />
                      ) : (
                        <ToggleLeft className="w-8 h-8" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={!!selectedReservation} onOpenChange={() => setSelectedReservation(null)}>
          <DialogContent className="bg-card">
            <DialogHeader>
              <DialogTitle>Update Reservation Status</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Tour:</strong> {selectedReservation?.tour_name}
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Guest:</strong> {selectedReservation?.guest_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Date:</strong> {selectedReservation?.date}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Admin Notes (Optional)</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any notes for this reservation..."
                  className="bg-background/50"
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setSelectedReservation(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleUpdateStatus("confirmed")}
                disabled={updating}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Confirm
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleUpdateStatus("rejected")}
                disabled={updating}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
